export const SHARED_PROFESSIONAL_ROLES = ["contractor", "architect"] as const
export type SharedProfessionalRole = (typeof SHARED_PROFESSIONAL_ROLES)[number]

export const MEMBERSHIP_ROLES = [
	"member",
	...SHARED_PROFESSIONAL_ROLES,
] as const
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number]

export const INVITATION_KINDS = ["email", "qr"] as const
export type InvitationKind = (typeof INVITATION_KINDS)[number]

export const PROPERTY_JOIN_REQUEST_STATUSES = [
	"pending_owner_approval",
	"accepted",
	"rejected",
] as const
export type PropertyJoinRequestStatus =
	(typeof PROPERTY_JOIN_REQUEST_STATUSES)[number]

export interface UserSummaryDTO {
	id: string
	email: string
	firstName: string | null
	lastName: string | null
	avatarUrl: string | null
}

export interface MembershipDTO {
	id: string
	user: UserSummaryDTO
	role: MembershipRole
	createdAt: string
}

export interface MembershipListResponse {
	owner: UserSummaryDTO
	memberships: MembershipDTO[]
}

export interface UpdateMembershipRoleBody {
	role: MembershipRole
}

export interface UpdateMembershipRoleResponse {
	membership: MembershipDTO
}

export interface InvitationDTO {
	id: string
	kind: InvitationKind
	/**
	 * The recipient email, or `null` for QR-kind invitations (delivered in
	 * person via QR code, no email channel).
	 */
	email: string | null
	role: MembershipRole
	expiresAt: string
	sentAt: string | null
	createdAt: string
	/**
	 * Populated when the invited email matches an existing onboarded
	 * user — the Share modal renders `${firstName} ${lastName} (${email})`
	 * in that case. `null` for unknown / not-yet-onboarded invitees, where
	 * the modal falls back to showing just the email. Always `null` for
	 * QR-kind invitations.
	 */
	recipient: UserSummaryDTO | null
}

export interface InvitationListResponse {
	invitations: InvitationDTO[]
}

export interface UpdateInvitationRoleBody {
	role: MembershipRole
}

export interface UpdateInvitationRoleResponse {
	invitation: InvitationDTO
}

/**
 * Request body for POST /properties/:id/invitations. Discriminated by `kind`:
 *  - kind="email" → `email` required, server queues a magic-link email.
 *  - kind="qr"    → `email` ignored; server returns a QR URL. New QR
 *                   invitations create owner-approval requests when scanned,
 *                   so no PIN is generated.
 *
 * `kind` defaults to "email" for back-compat with the original v1 wire shape.
 */
export type CreateInvitationBody =
	| { kind?: "email"; email: string; role: MembershipRole }
	| { kind: "qr"; role: MembershipRole }

/**
 * `pin` is always `null` for newly-created invitations. Legacy QR+PIN rows
 * can still exist in the database until expiry, but creating a QR no longer
 * returns PIN material.
 *
 * `qrToken` is populated iff `invitation.kind === "qr"` — the bearer secret
 * encoded into the QR payload. For email-kind invitations `qrToken` is `null`
 * (the token reaches the invitee via the magic-link email; the owner who
 * created the invitation never sees it, preventing them from accepting on
 * the invitee's behalf).
 *
 * `qrUrl` is the full HTTPS URL the QR should encode, derived from the
 * server's `APP_BASE_URL` env var (the same source the email task uses for
 * its magic-link). Populated iff `qrToken` is populated. Clients should NOT
 * reconstruct this URL from `window.location.origin` — the server is the
 * source of truth so the QR works across origins (web/Android share the
 * same target).
 */
export interface CreateInvitationResponse {
	invitation: InvitationDTO
	pin: string | null
	qrToken: string | null
	qrUrl: string | null
}

/**
 * Response body for POST /api/common/invitations/:id/resend.
 *
 * Resend is an email-only action: the controller rejects QR-kind invitations
 * with 400 before reaching the response stage, so this DTO does not carry
 * the `pin` / `qrToken` / `qrUrl` fields that [CreateInvitationResponse]
 * exposes for the QR branch. Web consumers invalidate the invitations list
 * query on success rather than reading this body, but the updated DTO is
 * returned for clients (e.g. a future mobile app) that prefer a single
 * round-trip update without a refetch.
 */
export interface ResendInvitationResponse {
	invitation: InvitationDTO
}

export interface InvitationContextResponse {
	invitation: {
		id: string
		kind: InvitationKind
		/** `null` for QR-kind invitations — see [InvitationDTO.email]. */
		email: string | null
		/**
		 * True only for legacy QR rows that still have stored PIN material.
		 * Newly-created QR invitations use owner approval and do not require
		 * the scanner to enter a PIN.
		 */
		requiresPin: boolean
		role: MembershipRole
		expiresAt: string
	}
	property: { id: string; name: string }
	inviter: { firstName: string | null; lastName: string | null }
}

/**
 * Request body for POST /invitations/:token/accept. `pin` is only used for
 * legacy QR+PIN rows that still have a stored PIN hash; email-kind invitations
 * authenticate the invitee via email match against the signed-in user.
 */
export interface AcceptInvitationBody {
	pin?: string
}

interface AcceptedInvitationResponse {
	membership: MembershipDTO
	property: { id: string; name: string }
}

export interface PendingOwnerApprovalInvitationResponse {
	state: "pending_owner_approval"
	joinRequest: {
		id: string
		status: "pending_owner_approval"
		role: MembershipRole
		createdAt: string
	}
	property: { id: string; name: string }
}

export type AcceptInvitationResponse =
	| AcceptedInvitationResponse
	| PendingOwnerApprovalInvitationResponse

export interface PropertyJoinRequestDTO {
	id: string
	status: PropertyJoinRequestStatus
	role: MembershipRole
	createdAt: string
	requester: UserSummaryDTO
}

export interface PropertyJoinRequestListResponse {
	joinRequests: PropertyJoinRequestDTO[]
}

export interface ApprovePropertyJoinRequestResponse {
	joinRequest: PropertyJoinRequestDTO
	membership: MembershipDTO
}

export interface RejectPropertyJoinRequestResponse {
	joinRequest: PropertyJoinRequestDTO
}
